/**
 * features/finance/FinanceOverview.tsx
 * ------------------------------------------------------------------
 * Finance landing — live KPIs, an at-a-glance "needs attention" list
 * with one-tap links into the screen that resolves each item, and
 * quick actions. Defaults its sense of "now" to the current term.
 * ------------------------------------------------------------------ */
import {
  Wallet, ReceiptText, TrendingUp, AlertTriangle, Users, Play, HandCoins,
  FileText, SlidersHorizontal, ChevronRight, CheckCircle2, type LucideIcon,
} from 'lucide-react';
import { GradientHero, SoftCard, StatChip } from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { ageInvoices, planArrears, fmtPula, fmtBalance, round2, FORM_BANDS } from '@/lib/financeCore';
import { SCHOOL, currentTermKey, termLabel } from '@/lib/school.config';
import type { TabId } from '@/components/Shell';

export default function FinanceOverview({ onNavigate }: { onNavigate?: (tab: TabId) => void }) {
  const data = useFinanceData();

  return (
    <div className="space-y-6">
      <GradientHero icon={Wallet} title="Finance" subtitle="Billing, collections and arrears at a glance" />
      {data.loading ? (
        <div className="flex justify-center py-16"><Spinner label="Loading finance…" /></div>
      ) : (
        <Body data={data} onNavigate={onNavigate} />
      )}
    </div>
  );
}

interface AttentionItem { icon: LucideIcon; tone: string; text: string; tab: TabId; cta: string; }

function Body({ data, onNavigate }: { data: ReturnType<typeof useFinanceData>; onNavigate?: (tab: TabId) => void }) {
  const term = currentTermKey();
  const currentYear = SCHOOL.selectableYears[0] ?? String(new Date().getFullYear());

  const live = data.invoices.filter((i) => i.status !== 'void');
  const billed = round2(live.reduce((a, i) => a + i.total, 0));
  const collected = round2(live.reduce((a, i) => a + i.paid, 0));
  const outstanding = round2(live.reduce((a, i) => a + i.balance, 0));
  const aging = ageInvoices(live, data.settings.agingThresholds);
  const overdue = round2(aging.total - aging.current);
  const families = new Set(live.map((i) => i.familyId).filter(Boolean)).size;
  const collectionRate = billed > 0 ? Math.round((collected / billed) * 100) : 0;

  // Attention signals
  const arrears = planArrears({ invoices: live, settings: data.settings });
  const unpaid = live.filter((i) => i.status === 'issued').length;
  const billedThisTerm = live.some((i) => i.term === term && i.year === currentYear);
  const missingBands = FORM_BANDS.filter((b) => !data.feeStructures.some((s) => s.year === currentYear && s.bandKey === b.key && s.active));

  const attention: AttentionItem[] = [];
  if (missingBands.length > 0) {
    attention.push({ icon: SlidersHorizontal, tone: 'amber', tab: 'financeFees', cta: 'Set up fees',
      text: `${missingBands.length} form band(s) have no fee structure for ${currentYear} — students in them can't be billed.` });
  }
  if (!billedThisTerm) {
    attention.push({ icon: Play, tone: 'blue', tab: 'financeBilling', cta: 'Run billing',
      text: `No invoices issued yet for ${termLabel(term)} ${currentYear}.` });
  }
  if (arrears.familyCount > 0) {
    attention.push({ icon: AlertTriangle, tone: 'red', tab: 'financeArrears', cta: 'Send reminders',
      text: `${arrears.familyCount} famil${arrears.familyCount === 1 ? 'y is' : 'ies are'} overdue — ${fmtPula(arrears.totalOverdue)} past due.` });
  }
  if (unpaid > 0) {
    attention.push({ icon: HandCoins, tone: 'teal', tab: 'financePayments', cta: 'Capture payments',
      text: `${unpaid} unpaid invoice(s) awaiting first payment.` });
  }

  const toneClass: Record<string, string> = {
    amber: 'text-amber-600 dark:text-amber-300', red: 'text-red-600 dark:text-red-400',
    blue: 'text-blue-600 dark:text-blue-300', teal: 'text-[var(--accent-link,#0d9488)]',
  };

  const quick: { label: string; icon: LucideIcon; tab: TabId }[] = [
    { label: 'Billing', icon: Play, tab: 'financeBilling' },
    { label: 'Payments', icon: HandCoins, tab: 'financePayments' },
    { label: 'Statements', icon: FileText, tab: 'financeStatements' },
    { label: 'Arrears', icon: AlertTriangle, tab: 'financeArrears' },
  ];

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatChip tone="blue" value={fmtPula(billed)} label="Billed" icon={ReceiptText} />
        <StatChip tone="emerald" value={fmtPula(collected)} label={`Collected · ${collectionRate}%`} icon={TrendingUp} />
        <StatChip tone="red" value={fmtBalance(outstanding)} label="Outstanding" icon={AlertTriangle} />
        <StatChip tone="slate" value={String(families)} label="Families billed" icon={Users} />
      </div>

      {/* Needs attention */}
      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Needs attention · {termLabel(term)} {currentYear}</h3>
        </div>
        <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
          {attention.length === 0 ? (
            <div className="px-5 py-6 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <CheckCircle2 size={18} className="text-emerald-500" /> All caught up — nothing needs action right now.
            </div>
          ) : attention.map((a, idx) => {
            const Icon = a.icon;
            return (
              <button key={idx} onClick={() => onNavigate?.(a.tab)}
                className="w-full text-left px-5 py-3.5 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                <Icon size={18} className={`shrink-0 ${toneClass[a.tone]}`} />
                <span className="flex-1 text-sm text-gray-700 dark:text-gray-200">{a.text}</span>
                <span className="hidden sm:inline text-xs font-medium text-[var(--accent-link,#0d9488)]">{a.cta}</span>
                <ChevronRight size={16} className="text-gray-300 dark:text-gray-600 shrink-0" />
              </button>
            );
          })}
        </div>
      </SoftCard>

      {/* Quick actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {quick.map((q) => {
          const Icon = q.icon;
          return (
            <button key={q.tab} onClick={() => onNavigate?.(q.tab)}
              className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-[var(--accent-link,#0d9488)] hover:bg-[var(--accent-link,#0d9488)]/5 transition-colors text-sm font-medium text-gray-700 dark:text-gray-200">
              <Icon size={16} className="text-[var(--accent-link,#0d9488)]" /> {q.label}
            </button>
          );
        })}
      </div>

      {/* Aging */}
      <SoftCard hover={false}>
        <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-3">Arrears aging</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
          <div>
            <div className="text-gray-400 text-xs">Current</div>
            <div className="font-semibold text-gray-800 dark:text-gray-100">{fmtPula(aging.current)}</div>
          </div>
          {aging.buckets.map((b) => (
            <div key={b.label}>
              <div className="text-gray-400 text-xs">{b.label}</div>
              <div className="font-semibold text-gray-800 dark:text-gray-100">{fmtPula(b.amount)}</div>
            </div>
          ))}
        </div>
        {overdue > 0 && (
          <button onClick={() => onNavigate?.('financeArrears')} className="mt-3 text-xs text-[var(--accent-link,#0d9488)] hover:underline">
            {fmtPula(overdue)} past due across {live.filter((i) => i.balance > 0).length} open invoice(s) — review arrears →
          </button>
        )}
      </SoftCard>

    </>
  );
}
